'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { analytics } from 'lib/analytics/analytics';
import { PictureIcon } from 'assets/icons';
import { useModal } from 'components/shared';
import { CarouselPillButton } from 'components/shared/CarouselPillButton';
import {
  ProductCarousel,
  ProductCarouselItem,
} from 'components/shared/ProductCarousel';
import { AirRingColors } from 'content/products/ring';
import { RealLifeImagesModalContent } from './RealLifeImagesModalContent';
import { CarouselFooterCard } from '../CarouselFooterCard';
import { OffersDataType } from '../OffersCarousel';
import styles from './RingAirProductCarousel.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

interface RingAirProductCarouselProps {
  className?: string;
  items: readonly ProductCarouselItem[];
  selectedColor: AirRingColors;
  sticky?: boolean;
  footerItems?: OffersDataType[];
}

export const RingAirProductCarousel = ({
  className,
  items,
  selectedColor,
  sticky = false,
  footerItems,
}: RingAirProductCarouselProps) => {
  const t = useTranslations('ring');
  const ModalClient = useModal();

  const handleImagePreview = useCallback(() => {
    analytics.track('real-life-images-clicked', {
      clicked: true,
    });
    ModalClient.set(
      <RealLifeImagesModalContent selectedColor={selectedColor} />,
    );
    ModalClient.setPortal(true);
    ModalClient.show();
  }, [ModalClient, selectedColor]);

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.carouselImageArea}>
        <ProductCarousel
          items={[...items]}
          sticky={sticky}
          buttonVariant="glass"
        />
        <CarouselPillButton
          className={styles.realImagesPill}
          icon={<PictureIcon />}
          label={t('ring.VIEW_REAL_LIFE')}
          onClick={handleImagePreview}
          aria-label={t('ring.VIEW_REAL_LIFE')}
        />
      </div>
      {footerItems && footerItems.length > 0 && (
        <CarouselFooterCard className={styles.carouselFooter} data={footerItems} />
      )}
    </div>
  );
};
